// ABOUTME: Badge component that displays today's behavior count on behavior buttons
// ABOUTME: nalu-v1 straddles the button's top-right corner (mockup position); other themes inset it

import React from 'react';
import { Badge, BadgeProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';

interface BehaviorCountBadgeProps {
  count: number;
  children: React.ReactNode;
  color?: BadgeProps['color'];
}

const BehaviorCountBadge: React.FC<BehaviorCountBadgeProps> = ({
  count,
  children,
  color = 'primary'
}) => {
  const theme = useTheme();
  const isNaluV1 = theme.logoSrc === naluV1Theme.logoSrc;
  // nalu-v1's mockup uses each behavior's darker palette shade for the badge background
  // (e.g. #46776F for Star/Reset, #B5853C for Triangle) with cream/white text on top —
  // MUI's default contrastText for these palette entries is black (chosen for WCAG AA
  // against the *lighter* .main/.light shades used elsewhere), which doesn't apply here
  // since the badge itself uses the darker shade, not .main.
  const naluV1PaletteKey = color && color !== 'default' ? color : 'primary';

  return (
    <Badge
      badgeContent={count}
      color={color}
      showZero
      max={99}
      sx={{
        '& .MuiBadge-badge': {
          fontSize: '0.9rem',
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          fontWeight: 600,
          // nalu-v1: no top/right override — MUI's own default badge transform
          // (translate(50%, -50%) off a top:0/right:0 base) already straddles the
          // wrapped element's corner; adding a custom offset on top of that
          // compounds into an unwanted floating gap above the button.
          ...(isNaluV1
            ? {
                // !important: MUI's own `.MuiBadge-color*` variant class (e.g.
                // .MuiBadge-colorSecondary) has equal CSS specificity to this sx-generated
                // selector, and empirically wins the resulting order tie — confirmed via
                // devtools showing the *.main color still computed despite this override.
                backgroundColor: `${theme.palette[naluV1PaletteKey].dark} !important`,
                color: `${theme.palette.background.default} !important`,
              }
            : { top: 6, right: 6 }),
        },
      }}
    >
      {children}
    </Badge>
  );
};

export default BehaviorCountBadge;